import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase, FUNCTIONS_URL } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, KeyRound, Webhook, Plus, Copy, Check, Trash2, AlertTriangle,
  Eye, EyeOff, ChevronDown, ChevronRight, BookOpen, RefreshCw,
} from 'lucide-react';

const WEBHOOK_EVENTS = [
  { id: 'issue.flagged', label: 'issue.flagged', desc: 'Fires when a new issue is flagged on a lesson.' },
  { id: 'assessment.completed', label: 'assessment.completed', desc: 'Fires when a quality assessment finishes for a lesson.' },
];

const bytesToHex = (bytes) => Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');

const randomHex = (numBytes) => {
  const bytes = new Uint8Array(numBytes);
  crypto.getRandomValues(bytes);
  return bytesToHex(bytes);
};

const sha256Hex = async (text) => {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return bytesToHex(new Uint8Array(digest));
};

const formatDate = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const CopyButton = ({ text, isDark }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };
  return (
    <button
      onClick={copy}
      className={`p-1.5 rounded-md border flex-shrink-0 transition-colors ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'}`}
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
};

const DeliveryStatusBadge = ({ status, isDark }) => {
  const styles = {
    delivered: isDark ? 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
    failed: isDark ? 'bg-amber-400/10 text-amber-400 border-amber-400/20' : 'bg-amber-50 text-amber-700 border-amber-200',
    dead: isDark ? 'bg-red-400/10 text-red-400 border-red-400/20' : 'bg-red-50 text-red-700 border-red-200',
    pending: isDark ? 'bg-[#27272a] text-[#a1a1aa] border-[#3f3f46]' : 'bg-[#f0f0f0] text-[#666] border-[#e5e5e5]',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

const DeveloperApi = () => {
  const { user, organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [keys, setKeys] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create key flow
  const [showKeyForm, setShowKeyForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null); // { name, fullKey }

  // Add endpoint flow
  const [showEndpointForm, setShowEndpointForm] = useState(false);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [endpointDesc, setEndpointDesc] = useState('');
  const [endpointEvents, setEndpointEvents] = useState(['issue.flagged', 'assessment.completed']);
  const [creatingEndpoint, setCreatingEndpoint] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null); // { url, secret }

  // Per-endpoint UI state
  const [visibleSecrets, setVisibleSecrets] = useState({}); // id -> bool
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [deliveries, setDeliveries] = useState({}); // endpoint_id -> rows
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const mutedText = isDark ? 'text-[#a1a1aa]' : 'text-[#666]';
  const faintText = isDark ? 'text-[#52525b]' : 'text-[#999]';
  const strongText = isDark ? 'text-white' : 'text-[#1a1a1a]';
  const inputClass = `w-full px-3 py-2 text-sm border rounded-lg ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`;
  const primaryBtn = `text-xs px-3 py-1.5 rounded-md font-medium ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'} disabled:opacity-50`;
  const secondaryBtn = `text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'} disabled:opacity-50`;

  const loadData = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const [keysRes, endpointsRes] = await Promise.all([
        supabase.from('api_keys')
          .select('id, name, key_prefix, scopes, created_at, last_used_at, revoked_at')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false }),
        supabase.from('webhook_endpoints')
          .select('id, url, secret, events, enabled, description, created_at, last_success_at, last_failure_at, consecutive_failures')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false }),
      ]);
      if (keysRes.error) throw keysRes.error;
      if (endpointsRes.error) throw endpointsRes.error;
      setKeys(keysRes.data || []);
      setEndpoints(endpointsRes.data || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load API settings');
    }
    setLoading(false);
  }, [organizationId]);

  useEffect(() => { loadData(); }, [loadData]);

  const createKey = async (e) => {
    e.preventDefault();
    const name = keyName.trim();
    if (!name) { toast.error('Give the key a name'); return; }
    setCreatingKey(true);
    try {
      const fullKey = `tt_live_${randomHex(32)}`;
      const keyHash = await sha256Hex(fullKey);
      const { error } = await supabase.from('api_keys').insert({
        organization_id: organizationId,
        name,
        key_prefix: fullKey.slice(0, 12),
        key_hash: keyHash,
        created_by: user?.id,
      });
      if (error) throw error;
      setRevealedKey({ name, fullKey });
      setKeyName('');
      setShowKeyForm(false);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to create key');
    }
    setCreatingKey(false);
  };

  const revokeKey = async (key) => {
    const ok = window.confirm(`Revoke API key "${key.name}"?\n\nAny integration using this key will immediately stop working. This cannot be undone.`);
    if (!ok) return;
    try {
      const { error } = await supabase.from('api_keys')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', key.id)
        .eq('organization_id', organizationId);
      if (error) throw error;
      toast.success('Key revoked');
      await loadData();
    } catch (e) {
      toast.error(e.message || 'Failed to revoke key');
    }
  };

  const toggleEvent = (eventId) => {
    setEndpointEvents(prev => prev.includes(eventId) ? prev.filter(e => e !== eventId) : [...prev, eventId]);
  };

  const createEndpoint = async (e) => {
    e.preventDefault();
    const url = endpointUrl.trim();
    if (!url.startsWith('https://')) { toast.error('Webhook URL must start with https://'); return; }
    if (endpointEvents.length === 0) { toast.error('Select at least one event'); return; }
    setCreatingEndpoint(true);
    try {
      const secret = `whsec_${randomHex(24)}`;
      const { error } = await supabase.from('webhook_endpoints').insert({
        organization_id: organizationId,
        url,
        secret,
        events: endpointEvents,
        enabled: true,
        description: endpointDesc.trim() || null,
      });
      if (error) throw error;
      setRevealedSecret({ url, secret });
      setEndpointUrl('');
      setEndpointDesc('');
      setEndpointEvents(['issue.flagged', 'assessment.completed']);
      setShowEndpointForm(false);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to add endpoint');
    }
    setCreatingEndpoint(false);
  };

  const toggleEndpoint = async (ep) => {
    try {
      const { error } = await supabase.from('webhook_endpoints')
        .update({ enabled: !ep.enabled })
        .eq('id', ep.id)
        .eq('organization_id', organizationId);
      if (error) throw error;
      setEndpoints(prev => prev.map(p => p.id === ep.id ? { ...p, enabled: !ep.enabled } : p));
    } catch (e) {
      toast.error(e.message || 'Failed to update endpoint');
    }
  };

  const deleteEndpoint = async (ep) => {
    const ok = window.confirm(`Delete webhook endpoint?\n\n${ep.url}\n\nNo further events will be delivered to it. This cannot be undone.`);
    if (!ok) return;
    try {
      const { error } = await supabase.from('webhook_endpoints')
        .delete()
        .eq('id', ep.id)
        .eq('organization_id', organizationId);
      if (error) throw error;
      toast.success('Endpoint deleted');
      if (expandedEndpoint === ep.id) setExpandedEndpoint(null);
      await loadData();
    } catch (e) {
      toast.error(e.message || 'Failed to delete endpoint');
    }
  };

  const toggleDeliveries = async (ep) => {
    if (expandedEndpoint === ep.id) { setExpandedEndpoint(null); return; }
    setExpandedEndpoint(ep.id);
    setDeliveriesLoading(true);
    try {
      const { data, error } = await supabase.from('webhook_deliveries')
        .select('id, event_type, status, attempts, response_status, created_at, delivered_at')
        .eq('endpoint_id', ep.id)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      setDeliveries(prev => ({ ...prev, [ep.id]: data || [] }));
    } catch (e) {
      toast.error(e.message || 'Failed to load deliveries');
    }
    setDeliveriesLoading(false);
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Developer
      </button>

      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${strongText}`}>API Access</h1>
        <button onClick={() => navigate('/developer/api-docs')} className={secondaryBtn}>
          <BookOpen className="h-3.5 w-3.5" /> API Documentation
        </button>
      </div>

      {/* ===================== API KEYS ===================== */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
            <KeyRound className={`w-5 h-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h2 className={`text-sm font-medium mb-1 ${strongText}`}>API Keys</h2>
            <p className={`text-xs ${mutedText}`}>
              Authenticate requests to the TutorTracer API with a bearer key. Keys are stored hashed — the full key is shown once, at creation.
            </p>
          </div>
          <button onClick={() => { setShowKeyForm(v => !v); setRevealedKey(null); }} className={primaryBtn}>
            <span className="flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Create key</span>
          </button>
        </div>

        {showKeyForm && (
          <form onSubmit={createKey} className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
            <label className={`block text-xs font-medium mb-1.5 ${strongText}`}>Key name</label>
            <div className="flex gap-2">
              <input
                value={keyName}
                onChange={e => setKeyName(e.target.value)}
                placeholder="e.g. Zapier integration"
                className={inputClass}
                autoFocus
              />
              <button type="submit" disabled={creatingKey || !keyName.trim()} className={primaryBtn}>
                {creatingKey ? 'Creating...' : 'Generate'}
              </button>
              <button type="button" onClick={() => setShowKeyForm(false)} className={secondaryBtn}>Cancel</button>
            </div>
          </form>
        )}

        {revealedKey && (
          <div className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-amber-400/5 border-amber-400/20' : 'bg-amber-50 border-amber-200'}`}>
            <div className={`flex items-center gap-2 text-xs font-bold mb-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              Copy this key now — it will never be shown again.
            </div>
            <p className={`text-xs mb-2 ${mutedText}`}>Key "{revealedKey.name}" was created. Store it somewhere safe (e.g. a secrets manager).</p>
            <div className="flex items-center gap-2">
              <code className={`flex-1 px-3 py-2 rounded-md text-xs font-mono break-all ${isDark ? 'bg-[#0a0a0a] text-white border border-[#27272a]' : 'bg-white text-[#1a1a1a] border border-[#e5e5e5]'}`}>
                {revealedKey.fullKey}
              </code>
              <CopyButton text={revealedKey.fullKey} isDark={isDark} />
            </div>
            <button onClick={() => setRevealedKey(null)} className={`mt-2 text-[11px] underline ${mutedText}`}>
              I've saved it — dismiss
            </button>
          </div>
        )}

        {loading ? (
          <p className={`text-xs ${mutedText}`}>Loading...</p>
        ) : keys.length === 0 ? (
          <p className={`text-xs ${faintText}`}>No API keys yet. Create one to start using the API.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className={`text-[10px] uppercase tracking-wide ${faintText}`}>
                  <th className="pb-2 pr-4 font-medium">Name</th>
                  <th className="pb-2 pr-4 font-medium">Key</th>
                  <th className="pb-2 pr-4 font-medium">Created</th>
                  <th className="pb-2 pr-4 font-medium">Last used</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map(key => {
                  const revoked = !!key.revoked_at;
                  return (
                    <tr key={key.id} className={`border-t text-xs ${isDark ? 'border-[#27272a]' : 'border-[#f0f0f0]'} ${revoked ? 'opacity-45' : ''}`}>
                      <td className={`py-2.5 pr-4 font-medium ${strongText}`}>{key.name}</td>
                      <td className={`py-2.5 pr-4 font-mono ${mutedText}`}>{key.key_prefix}&hellip;</td>
                      <td className={`py-2.5 pr-4 ${mutedText}`}>{formatDate(key.created_at)}</td>
                      <td className={`py-2.5 pr-4 ${mutedText}`}>{formatDate(key.last_used_at) || 'never'}</td>
                      <td className="py-2.5 pr-4">
                        {revoked ? (
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${isDark ? 'bg-red-400/10 text-red-400 border-red-400/20' : 'bg-red-50 text-red-700 border-red-200'}`}>Revoked</span>
                        ) : (
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${isDark ? 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>Active</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right">
                        {!revoked && (
                          <button onClick={() => revokeKey(key)} className={`text-[11px] px-2 py-1 rounded-md ${isDark ? 'text-red-400 hover:bg-red-400/10' : 'text-red-600 hover:bg-red-50'}`}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===================== WEBHOOKS ===================== */}
      <div className={`${cardClass} p-5`}>
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#18181b]' : 'bg-[#f0f0f0]'}`}>
            <Webhook className={`w-5 h-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h2 className={`text-sm font-medium mb-1 ${strongText}`}>Webhook Endpoints</h2>
            <p className={`text-xs ${mutedText}`}>
              Receive HTTPS callbacks when events happen in your organisation. Payloads are signed with the endpoint's secret — see the docs for verification.
            </p>
          </div>
          <button onClick={() => { setShowEndpointForm(v => !v); setRevealedSecret(null); }} className={primaryBtn}>
            <span className="flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> Add endpoint</span>
          </button>
        </div>

        {showEndpointForm && (
          <form onSubmit={createEndpoint} className={`mb-4 p-4 rounded-lg border space-y-3 ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${strongText}`}>Endpoint URL</label>
              <input
                value={endpointUrl}
                onChange={e => setEndpointUrl(e.target.value)}
                placeholder="https://example.com/webhooks/tutortracer"
                className={inputClass}
                autoFocus
              />
              {endpointUrl && !endpointUrl.trim().startsWith('https://') && (
                <p className="text-[11px] text-red-500 mt-1">URL must start with https://</p>
              )}
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${strongText}`}>Events</label>
              <div className="space-y-1.5">
                {WEBHOOK_EVENTS.map(ev => (
                  <label key={ev.id} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={endpointEvents.includes(ev.id)}
                      onChange={() => toggleEvent(ev.id)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className={`text-xs font-mono ${strongText}`}>{ev.label}</span>
                      <span className={`block text-[11px] ${faintText}`}>{ev.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${strongText}`}>Description <span className={`font-normal ${faintText}`}>(optional)</span></label>
              <input
                value={endpointDesc}
                onChange={e => setEndpointDesc(e.target.value)}
                placeholder="e.g. Slack alerting bridge"
                className={inputClass}
              />
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={creatingEndpoint} className={primaryBtn}>
                {creatingEndpoint ? 'Adding...' : 'Add endpoint'}
              </button>
              <button type="button" onClick={() => setShowEndpointForm(false)} className={secondaryBtn}>Cancel</button>
            </div>
          </form>
        )}

        {revealedSecret && (
          <div className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-amber-400/5 border-amber-400/20' : 'bg-amber-50 border-amber-200'}`}>
            <div className={`flex items-center gap-2 text-xs font-bold mb-2 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              Signing secret for {revealedSecret.url}
            </div>
            <p className={`text-xs mb-2 ${mutedText}`}>Use this to verify the <code className="font-mono text-[11px]">X-TutorTracer-Signature</code> header on incoming deliveries. Admins can re-reveal it from the endpoint row.</p>
            <div className="flex items-center gap-2">
              <code className={`flex-1 px-3 py-2 rounded-md text-xs font-mono break-all ${isDark ? 'bg-[#0a0a0a] text-white border border-[#27272a]' : 'bg-white text-[#1a1a1a] border border-[#e5e5e5]'}`}>
                {revealedSecret.secret}
              </code>
              <CopyButton text={revealedSecret.secret} isDark={isDark} />
            </div>
            <button onClick={() => setRevealedSecret(null)} className={`mt-2 text-[11px] underline ${mutedText}`}>
              I've saved it — dismiss
            </button>
          </div>
        )}

        {loading ? (
          <p className={`text-xs ${mutedText}`}>Loading...</p>
        ) : endpoints.length === 0 ? (
          <p className={`text-xs ${faintText}`}>No webhook endpoints configured.</p>
        ) : (
          <div className="space-y-3">
            {endpoints.map(ep => (
              <div key={ep.id} className={`rounded-lg border ${isDark ? 'bg-[#0a0a0a] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]'}`}>
                <div className="p-3.5">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-mono break-all ${strongText}`}>{ep.url}</span>
                        {!ep.enabled && (
                          <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full border ${isDark ? 'bg-[#27272a] text-[#a1a1aa] border-[#3f3f46]' : 'bg-[#f0f0f0] text-[#666] border-[#e5e5e5]'}`}>Disabled</span>
                        )}
                      </div>
                      {ep.description && <p className={`text-[11px] mt-0.5 ${mutedText}`}>{ep.description}</p>}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        {(ep.events || []).map(ev => (
                          <span key={ev} className={`inline-block px-2 py-0.5 text-[10px] font-mono rounded-full border ${isDark ? 'bg-[#18181b] text-[#a1a1aa] border-[#27272a]' : 'bg-white text-[#666] border-[#e5e5e5]'}`}>
                            {ev}
                          </span>
                        ))}
                      </div>
                      {ep.consecutive_failures > 0 && (
                        <div className={`flex items-center gap-1.5 mt-2 text-[11px] ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                          {ep.consecutive_failures} consecutive failure{ep.consecutive_failures === 1 ? '' : 's'} — endpoint auto-disables at 20.
                        </div>
                      )}
                      <div className={`flex items-center gap-3 mt-1.5 text-[11px] ${faintText}`}>
                        <span>Last success: {formatDate(ep.last_success_at) || 'never'}</span>
                        <span>Last failure: {formatDate(ep.last_failure_at) || 'never'}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Enabled toggle */}
                      <button
                        onClick={() => toggleEndpoint(ep)}
                        role="switch"
                        aria-checked={ep.enabled}
                        title={ep.enabled ? 'Disable endpoint' : 'Enable endpoint'}
                        className={`relative w-9 h-5 rounded-full transition-colors ${ep.enabled ? (isDark ? 'bg-[#7662ac]' : 'bg-[#586f8d]') : (isDark ? 'bg-[#27272a]' : 'bg-[#d4d4d4]')}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${ep.enabled ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                      <button
                        onClick={() => deleteEndpoint(ep)}
                        title="Delete endpoint"
                        className={`p-1.5 rounded-md ${isDark ? 'text-red-400 hover:bg-red-400/10' : 'text-red-600 hover:bg-red-50'}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Secret reveal */}
                  <div className="flex items-center gap-2 mt-2.5">
                    <button
                      onClick={() => setVisibleSecrets(prev => ({ ...prev, [ep.id]: !prev[ep.id] }))}
                      className={`flex items-center gap-1.5 text-[11px] ${mutedText} hover:underline`}
                    >
                      {visibleSecrets[ep.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {visibleSecrets[ep.id] ? 'Hide signing secret' : 'Reveal signing secret'}
                    </button>
                    {visibleSecrets[ep.id] && (
                      <>
                        <code className={`px-2 py-0.5 rounded text-[11px] font-mono break-all ${isDark ? 'bg-[#18181b] text-white' : 'bg-white text-[#1a1a1a] border border-[#e5e5e5]'}`}>
                          {ep.secret}
                        </code>
                        <CopyButton text={ep.secret} isDark={isDark} />
                      </>
                    )}
                  </div>

                  {/* Deliveries expander */}
                  <button
                    onClick={() => toggleDeliveries(ep)}
                    className={`flex items-center gap-1 mt-2 text-[11px] ${mutedText} hover:underline`}
                  >
                    {expandedEndpoint === ep.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    Recent deliveries
                  </button>
                </div>

                {expandedEndpoint === ep.id && (
                  <div className={`border-t px-3.5 py-3 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                    {deliveriesLoading && !deliveries[ep.id] ? (
                      <p className={`text-[11px] ${mutedText} flex items-center gap-1.5`}><RefreshCw className="h-3 w-3 animate-spin" /> Loading deliveries...</p>
                    ) : (deliveries[ep.id] || []).length === 0 ? (
                      <p className={`text-[11px] ${faintText}`}>No deliveries yet for this endpoint.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead>
                            <tr className={`text-[10px] uppercase tracking-wide ${faintText}`}>
                              <th className="pb-1.5 pr-4 font-medium">Event</th>
                              <th className="pb-1.5 pr-4 font-medium">Status</th>
                              <th className="pb-1.5 pr-4 font-medium">Attempts</th>
                              <th className="pb-1.5 pr-4 font-medium">HTTP</th>
                              <th className="pb-1.5 font-medium">Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deliveries[ep.id].map(d => (
                              <tr key={d.id} className={`border-t text-[11px] ${isDark ? 'border-[#27272a]' : 'border-[#f0f0f0]'}`}>
                                <td className={`py-2 pr-4 font-mono ${strongText}`}>{d.event_type}</td>
                                <td className="py-2 pr-4"><DeliveryStatusBadge status={d.status} isDark={isDark} /></td>
                                <td className={`py-2 pr-4 ${mutedText}`}>{d.attempts}</td>
                                <td className={`py-2 pr-4 ${mutedText}`}>{d.response_status ?? '—'}</td>
                                <td className={`py-2 ${mutedText}`}>{formatDate(d.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className={`mt-4 text-[11px] px-3 py-2 rounded-lg border ${isDark ? 'bg-[#0a0a0a] border-[#27272a] text-[#a1a1aa]' : 'bg-[#fafafa] border-[#e5e5e5] text-[#666]'}`}>
          Tip: a test receiver is deployed at{' '}
          <code className="font-mono text-[10px]">{`${FUNCTIONS_URL}/webhook-echo`}</code>{' '}
          — add it as an endpoint to verify the delivery pipeline end to end.
        </div>
      </div>
    </div>
  );
};

export default DeveloperApi;
